import React, { useState } from "react";
import { Box, Button, Card, CardContent, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ProductImage from "../components/ProductImage";
import { ProductVM } from "../data/types";
import { formatCAD } from "../utils/currency";
import { foodImageDataUri } from "../utils/foodImage";

interface Props {
  product: ProductVM;
  /** Units of this product already in the cart (for the "in cart" hint). */
  qty: number;
  compact?: boolean;
  /** Add a chosen quantity + unit to the order (QA #5/#7). */
  onAdd: (id: number, qty: number, unit: "CASE" | "UNIT") => void;
}

const LOW_STOCK = 10;
const round2 = (v: number) => Math.round(v * 100) / 100;

const ProductCard: React.FC<Props> = ({ product, qty, compact, onAdd }) => {
  const stock = product.stock ?? 0;
  const inStock = stock > 0;
  const low = inStock && stock <= LOW_STOCK;
  const upc = product.unitsPerCase && product.unitsPerCase > 0 ? product.unitsPerCase : 1;
  const canUnit = upc > 1;

  const [unit, setUnit] = useState<"CASE" | "UNIT">("CASE");
  const [sel, setSel] = useState(1);
  const max = unit === "UNIT" ? stock * upc : stock;      // stock is tracked in cases
  const clamped = Math.min(Math.max(1, sel), Math.max(1, max));
  const unitPrice = unit === "UNIT" ? round2(product.price / upc) : product.price;

  const stockLabel = !inStock ? "Out of Stock" : low ? `Low stock (${stock})` : "In Stock";
  const stockColor = !inStock ? "error.main" : low ? "warning.main" : "success.main";

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
      <Box sx={{ p: 1.5, pb: 0, position: "relative" }}>
        <ProductImage src={product.image} fallback={foodImageDataUri(product.name, product.category)} alt={product.name} />
        <IconButton size="small" sx={{ position: "absolute", top: 18, right: 18, bgcolor: "rgba(255,255,255,.9)" }}>
          <FavoriteBorderIcon fontSize="small" />
        </IconButton>
      </Box>
      <CardContent sx={{ pt: 1.25, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
        <Typography variant="subtitle2" noWrap title={product.name}>{product.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {product.packagingUnit}{canUnit ? ` · ${upc} units/case` : ""}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {formatCAD(unitPrice)}<Typography component="span" variant="caption" color="text.secondary">{unit === "UNIT" ? " / unit" : " / case"}</Typography>
        </Typography>

        {!compact && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary">SKU: {product.sku}</Typography>
            <Typography variant="caption" sx={{ color: stockColor, fontWeight: 600 }}>● {stockLabel}</Typography>
          </Stack>
        )}

        {!compact && canUnit && (
          <ToggleButtonGroup
            exclusive size="small" value={unit}
            onChange={(_, v) => { if (v) { setUnit(v); setSel(1); } }}
            sx={{ mt: 0.5 }}
          >
            <ToggleButton value="CASE">Case</ToggleButton>
            <ToggleButton value="UNIT">Unit</ToggleButton>
          </ToggleButtonGroup>
        )}

        <Box sx={{ flex: 1 }} />

        {!compact && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
            <IconButton size="small" disabled={!inStock || clamped <= 1}
              onClick={() => setSel((q) => Math.max(1, q - 1))}
              sx={{ border: "1px solid", borderColor: "divider" }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 40, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 1, py: 0.5 }}>
              {inStock ? clamped : 0}
            </Box>
            <IconButton size="small" disabled={!inStock || clamped >= max}
              onClick={() => setSel((q) => Math.min(max, q + 1))}
              sx={{ border: "1px solid", borderColor: "divider" }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}

        <Button
          fullWidth variant="contained" disabled={!inStock}
          onClick={() => onAdd(product.id, compact ? 1 : clamped, compact ? "CASE" : unit)}
        >
          Add to Order{qty > 0 ? ` (${qty} in cart)` : ""}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
